import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

const POLL_INTERVAL = 30000;
const STORAGE_KEY = 'lbs_seen_friend_req';

const getSeenIds = () => {
  try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '[]'); }
  catch { return []; }
};
const addSeenId = (id) => {
  const seen = getSeenIds();
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...seen, id]));
};

const Avatar = ({ name }) => {
  const colors = ['#0ea5e9','#6366f1','#10b981','#f59e0b','#ec4899','#8b5cf6'];
  const bg = colors[(name?.charCodeAt(0) || 0) % colors.length];
  return (
    <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0" style={{ backgroundColor: bg }}>
      {name?.[0]?.toUpperCase() || '?'}
    </div>
  );
};

const FriendRequestNotification = () => {
  const { user } = useAuth();
  const [current, setCurrent] = useState(null);
  const [acting, setActing] = useState(false);
  const [visible, setVisible] = useState(false);
  const queueRef = useRef([]);

  const showNext = useCallback(() => {
    if (queueRef.current.length > 0) {
      const next = queueRef.current.shift();
      setCurrent(next);
      requestAnimationFrame(() => setVisible(true));
    } else {
      setCurrent(null);
      setVisible(false);
    }
  }, []);

  const dismiss = useCallback((id) => {
    addSeenId(id);
    setVisible(false);
    setTimeout(() => {
      setCurrent(null);
      showNext();
    }, 300);
  }, [showNext]);

  const handleAccept = async () => {
    if (!current || acting) return;
    setActing(true);
    try { await api.put(`/friends/${current._id}/accept`); } catch {}
    setActing(false);
    dismiss(current._id);
  };

  const handleRefuse = async () => {
    if (!current || acting) return;
    setActing(true);
    try { await api.delete(`/friends/${current._id}`); } catch {}
    setActing(false);
    dismiss(current._id);
  };

  const checkRequests = useCallback(async () => {
    if (!user) return;
    try {
      const res = await api.get('/friends/requests');
      const seen = getSeenIds();
      const fresh = res.data.filter(
        (r) => !seen.includes(r._id) && !queueRef.current.some((q) => q._id === r._id) && (!current || current._id !== r._id)
      );
      if (fresh.length > 0) {
        queueRef.current = [...queueRef.current, ...fresh];
        if (!current) showNext();
      }
    } catch {}
  }, [user, current, showNext]);

  useEffect(() => {
    checkRequests();
    const id = setInterval(checkRequests, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [checkRequests]);

  if (!current) return null;

  const name = `${current.requester?.firstName || ''} ${current.requester?.lastName || ''}`.trim() || current.requester?.email || '?';

  return (
    <div
      className={`fixed top-20 right-4 z-50 w-80 transition-all duration-300 ease-out ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-3 pointer-events-none'
      }`}
    >
      <div className="bg-[#111318] border border-white/[0.10] rounded-2xl shadow-2xl shadow-black/70 p-4 flex flex-col gap-3">
        <div className="flex items-start gap-3">
          <Avatar name={name} />
          <div className="flex-1 min-w-0">
            <p className="text-[#dce4ef] font-semibold text-sm leading-snug">{name}</p>
            <p className="text-[#4d6082] text-xs mt-0.5">t'a envoyé une invitation d'ami</p>
          </div>
          <button
            onClick={() => dismiss(current._id)}
            className="text-[#2a3c52] hover:text-[#4d6082] transition-colors shrink-0 mt-0.5"
            aria-label="Fermer"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div className="flex gap-2">
          <button
            onClick={handleRefuse}
            disabled={acting}
            className="flex-1 py-2 rounded-lg text-xs font-medium bg-white/[0.05] hover:bg-white/[0.09] text-[#4d6082] hover:text-[#8a9bb5] border border-white/[0.08] transition-all disabled:opacity-50"
          >
            Refuser
          </button>
          <button
            onClick={handleAccept}
            disabled={acting}
            className="flex-1 py-2 rounded-lg text-xs font-semibold bg-sky-500 hover:bg-sky-400 text-white transition-all disabled:opacity-50 shadow-sm shadow-sky-500/30"
          >
            {acting ? '…' : 'Accepter'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FriendRequestNotification;
